import { useTranslation } from 'react-i18next';

import { PlayButtonIcon } from '../../components';

/**
 * 진단 이어하기 팝업(시안 `1048:48431`) — 진행 중인 회차가 있을 때 홈에 들어서면 뜬다.
 *
 * 시안은 버튼 둘뿐이지만 배경을 눌러 닫을 수 있게 둔다: 홈에 머물 길이 없으면 더보기·이력에
 * 못 간다. 닫으면 카드 CTA가 `이어서 진단하기`로 남아 길을 잃지 않는다. 키보드 닫기는 없다 —
 * 웹의 다른 모달(RecordReviewView)과 같은 계약이고, 포커스 트랩 없이 Esc만 다는 건 반쪽이다.
 *
 * 상태 줄 색은 시안의 파랑 대신 레드다 — 파랑은 다른 화면 팔레트에 없어 SCREENS §10-11이
 * 확인 항목으로 올려 둔 색이다.
 */

interface ResumeDialogProps {
  /** 지금 서 있는 단계(1-기준) — 마친 수 + 1. */
  current: number;
  total: number;
  onRestart: () => void;
  onResume: () => void;
  onDismiss: () => void;
}

export const ResumeDialog = ({
  current,
  total,
  onRestart,
  onResume,
  onDismiss,
}: ResumeDialogProps) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
      <button
        type="button"
        data-testid="resume-backdrop"
        aria-label={t('web.home.resume.dismiss')}
        onClick={onDismiss}
        className="absolute inset-0 bg-black/40"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="resume-dialog-title"
        className="relative w-full max-w-sm rounded-3xl bg-popover p-6 text-center shadow-xl"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <PlayButtonIcon className="h-6 w-6 text-primary" aria-hidden="true" />
        </div>
        <h2 id="resume-dialog-title" className="mt-4 text-lg font-bold">
          {t('web.home.resume.title')}
        </h2>
        <p className="mt-1 text-sm font-semibold tabular-nums text-primary">
          {t('web.home.resume.status', { current, total })}
        </p>
        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
          {t('web.home.resume.body')}
        </p>
        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={onRestart}
            className="min-h-[48px] flex-1 rounded-full border border-border bg-background text-sm font-semibold"
          >
            {t('web.home.resume.restart')}
          </button>
          <button
            type="button"
            autoFocus
            onClick={onResume}
            className="min-h-[48px] flex-1 rounded-full bg-[var(--bk-900)] text-sm font-semibold text-white"
          >
            {t('web.home.resume.continue_')}
          </button>
        </div>
      </div>
    </div>
  );
};
